<template>
  <div class="limiter-content voting-page pa-3 pa-lg-0">
    <div class="padding-block"></div>

    <header class="voting-intro">
      <span class="voting-eyebrow"><v-icon small color="primary">mdi-vote-outline</v-icon> Assembleia geral de credores</span>
      <h1>Seu voto. <span>Sua participação.</span></h1>
      <p>Acompanhe a deliberação e registre sua decisão.</p>
    </header>

    <login-required-notice v-if="!user" />

    <v-row v-else justify="center">
      <v-col cols="12">

        <v-card class="voting-panel" flat>

          <v-card-text>
            <template v-if="canShowVoting">
            <section class="meeting-summary" aria-label="Detalhes da assembleia">
              <span class="meeting-eyebrow">EM DELIBERAÇÃO</span>
              <div class="meeting-context">{{ dataset.data.client_name }} <span aria-hidden="true">/</span> {{ dataset.data.general_meeting_name }}</div>
              <div class="meeting-heading">
                <h2>{{ dataset.data.voting_name || 'Detalhes da votação' }}</h2>
                <v-chip v-if="dataset.data.voting_status === 2" small class="live-status" outlined><v-icon left small>mdi-circle-small</v-icon>Em andamento</v-chip>
                <v-chip v-else-if="dataset.data.voting_status === 3" small color="warning" outlined>Pausada</v-chip>
                <v-chip v-else-if="dataset.data.voting_status === 4" small outlined>Finalizada · em conferência</v-chip>
                <v-chip v-else-if="dataset.data.voting_status === 5" small color="green darken-3" outlined>Resultado publicado</v-chip>
                <v-chip v-else-if="dataset.data.status === 1" small outlined>Sem votação</v-chip>
              </div>
              <p v-if="dataset.data.voting_description" class="meeting-description">{{ dataset.data.voting_description }}</p>
            </section>
            <div v-if="!isProcurator && dataset.credits.length" class="voter-status" role="status">
              <v-icon small :color="pendingCreditsCount ? 'primary' : 'green darken-3'">{{ pendingCreditsCount ? 'mdi-vote-outline' : 'mdi-check-circle-outline' }}</v-icon>
              <span v-if="!pendingCreditsCount">{{ dataset.credits.length === 1 ? 'Seu voto foi registrado.' : 'Todos os seus votos foram registrados.' }}</span>
              <span v-else-if="dataset.data.voting_status === 2">{{ dataset.credits.length === 1 ? 'Selecione uma opção e confirme seu voto abaixo.' : pendingCreditsCount + ' de ' + dataset.credits.length + ' créditos aguardam seu voto.' }}</span>
              <span v-else>{{ pendingCreditsCount }} crédito(s) sem voto registrado.</span>
            </div>

            <div v-if="isProcurator" class="ballot-section">
              <div v-if="!ballot.creditors.length" class="ballot-notice">
                <v-icon small color="#7e55ab">mdi-information-outline</v-icon>
                Nenhum credor representado com presença confirmada nesta votação.
              </div>

              <template v-else>
                <div class="ballot-notice">
                  <v-icon small color="#7e55ab">mdi-information-outline</v-icon>
                  Cada linha é um crédito (classe) de um credor representado. O voto é registrado por classe.
                </div>

                <div v-if="dataset.data.voting_status === 2 && ballotPendingCount > 0" class="bulk-vote">
                  <div class="bulk-vote__header">
                    <div>
                      <div class="bulk-vote__title">Votar igual em todos os créditos pendentes</div>
                      <div class="bulk-vote__text">
                        Marca a mesma opção nos <strong>{{ ballotPendingCount }}</strong> crédito(s) ainda sem voto.
                        Nada é registrado até você clicar em <strong>Registrar votos</strong>.
                        Se preferir, escolha linha a linha na tabela abaixo.
                      </div>
                    </div>
                  </div>
                  <div class="bulk-vote__actions">
                    <v-btn outlined color="green darken-1" class="ballot-btn" :disabled="dataset.loading" @click="confirmApplyToAll('yes')">
                      <v-icon left small>mdi-thumb-up</v-icon>SIM em todos
                    </v-btn>
                    <v-btn outlined color="red darken-1" class="ballot-btn" :disabled="dataset.loading" @click="confirmApplyToAll('no')">
                      <v-icon left small>mdi-thumb-down</v-icon>NÃO em todos
                    </v-btn>
                    <v-btn outlined color="grey darken-1" class="ballot-btn" :disabled="dataset.loading" @click="confirmApplyToAll('null')">
                      <v-icon left small>mdi-cancel</v-icon>ABSTENÇÃO em todos
                    </v-btn>
                  </div>
                  <div class="bulk-vote__footer">
                    <span>
                      <strong>{{ ballotSelectedCount }}</strong> de <strong>{{ ballotPendingCount }}</strong> crédito(s) pendente(s) com opção marcada
                    </span>
                    <span class="bulk-vote__footer-actions">
                      <v-btn v-if="ballotSelectedCount" text class="ballot-btn" :disabled="dataset.loading" @click="clearPendingSelection">Limpar seleção</v-btn>
                      <v-btn color="primary" depressed class="ballot-btn" :loading="dataset.loading" :disabled="!ballotSelectedCount" @click="submitBallot">
                        Registrar {{ ballotSelectedCount || '' }} voto(s)
                      </v-btn>
                    </span>
                  </div>
                </div>

                <!-- Celular: um cartao por credito, sem rolagem lateral -->
                <div v-if="$vuetify.breakpoint.xsOnly" class="ballot-cards">
                  <template v-for="creditor in ballot.creditors">
                    <article v-for="credit in creditor.credits" :key="`card-${credit.user_creditor_id}`" class="ballot-card">
                      <div class="ballot-card__head">
                        <div class="ballot-card__creditor">
                          <strong>{{ creditor.name }}</strong>
                          <span>{{ formatRegistration(creditor.registration) }}</span>
                        </div>
                        <div class="ballot-card__amount">{{ formatCreditAmount(credit.amount, credit.currency) }}</div>
                      </div>
                      <div class="ballot-card__class">{{ credit.class_name }}</div>

                      <div v-if="credit.vote" class="ballot-card__voted">
                        <v-chip small outlined :color="voteColor(credit.vote)"><strong>{{ voteLabel(credit.vote) }}</strong></v-chip>
                        <span v-if="credit.vote_locked">Termo de adesão · voto irrevogável</span>
                        <span v-if="credit.reservation" :title="credit.reservation.text">Com ressalva</span>
                        <v-spacer></v-spacer>
                        <v-btn v-if="dataset.data.voting_status === 2 && !credit.vote_locked" small text color="primary" class="ballot-card__action" :loading="dataset.loading" @click="restoreVote(credit)">
                          <v-icon left small>mdi-restore</v-icon>Restaurar
                        </v-btn>
                      </div>
                      <template v-else-if="dataset.data.voting_status === 2">
                        <div class="ballot-card__options">
                          <button type="button" class="ballot-option ballot-option--yes" :class="{ 'ballot-option--on': pendingVotes[credit.user_creditor_id] === 'yes' }"
                            :aria-pressed="String(pendingVotes[credit.user_creditor_id] === 'yes')" @click="setVote(credit, 'yes')">
                            <v-icon small>mdi-thumb-up</v-icon>SIM
                          </button>
                          <button type="button" class="ballot-option ballot-option--no" :class="{ 'ballot-option--on': pendingVotes[credit.user_creditor_id] === 'no' }"
                            :aria-pressed="String(pendingVotes[credit.user_creditor_id] === 'no')" @click="setVote(credit, 'no')">
                            <v-icon small>mdi-thumb-down</v-icon>NÃO
                          </button>
                          <button type="button" class="ballot-option ballot-option--null" :class="{ 'ballot-option--on': pendingVotes[credit.user_creditor_id] === 'null' }"
                            :aria-pressed="String(pendingVotes[credit.user_creditor_id] === 'null')" @click="setVote(credit, 'null')">
                            <v-icon small>mdi-cancel</v-icon>ABSTENÇÃO
                          </button>
                        </div>
                        <v-btn small text :color="pendingReservations[credit.user_creditor_id] ? 'success' : 'primary'" class="ballot-card__action mt-2" @click="openReservation(credit)">
                          <v-icon left small>{{ pendingReservations[credit.user_creditor_id] ? 'mdi-text-box-check-outline' : 'mdi-text-box-plus-outline' }}</v-icon>
                          {{ pendingReservations[credit.user_creditor_id] ? 'Ressalva adicionada' : 'Adicionar ressalva' }}
                        </v-btn>
                      </template>
                      <span v-else class="grey--text">Não votou</span>
                    </article>
                  </template>
                </div>

                <v-simple-table v-else class="ballot-table">
                  <template #default>
                    <thead>
                      <tr>
                        <th>Credor</th>
                        <th>Classe</th>
                        <th class="text-right">Crédito</th>
                        <th class="text-center">Voto</th>
                        <th v-if="dataset.data.voting_status === 2" class="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      <template v-for="creditor in ballot.creditors">
                        <tr v-for="(credit, index) in creditor.credits" :key="credit.user_creditor_id">
                          <td>
                            <template v-if="index === 0">
                              <strong>{{ creditor.name }}</strong>
                              <div class="caption grey--text">{{ formatRegistration(creditor.registration) }}</div>
                            </template>
                          </td>
                          <td>{{ credit.class_name }}</td>
                          <td class="text-right">{{ formatCreditAmount(credit.amount, credit.currency) }}</td>
                          <td class="text-center">
                            <template v-if="credit.vote"><v-chip small outlined :color="voteColor(credit.vote)">
                              <strong>{{ voteLabel(credit.vote) }}</strong>
                            </v-chip>
                            <span v-if="credit.vote_locked" class="d-block text-caption">Termo de adesão · voto irrevogável</span>
                            <span v-if="credit.reservation" class="d-block text-caption" :title="credit.reservation.text">Com ressalva</span></template>
                            <div v-else-if="dataset.data.voting_status === 2" class="ballot-vote-options">
                              <v-chip color="green" outlined small
                                :input-value="pendingVotes[credit.user_creditor_id] === 'yes'"
                                @click="setVote(credit, 'yes')">
                                <v-icon left small>mdi-thumb-up</v-icon>SIM
                              </v-chip>
                              <v-chip color="red" outlined small
                                :input-value="pendingVotes[credit.user_creditor_id] === 'no'"
                                @click="setVote(credit, 'no')">
                                <v-icon left small>mdi-thumb-down</v-icon>NÃO
                              </v-chip>
                              <v-chip color="grey" outlined small
                                :input-value="pendingVotes[credit.user_creditor_id] === 'null'"
                                @click="setVote(credit, 'null')">
                                <v-icon left small>mdi-cancel</v-icon>ABSTENÇÃO
                              </v-chip>
                            </div>
                            <span v-else class="grey--text">Não votou</span>
                          </td>
                          <td v-if="dataset.data.voting_status === 2" class="text-center">
                            <v-btn v-if="credit.vote && !credit.vote_locked" x-small text color="primary" :loading="dataset.loading" @click="restoreVote(credit)">
                              <v-icon left small>mdi-restore</v-icon>Restaurar
                            </v-btn>
                            <v-btn v-if="!credit.vote" x-small text :color="pendingReservations[credit.user_creditor_id] ? 'success' : 'primary'" @click="openReservation(credit)">
                              <v-icon left small>{{ pendingReservations[credit.user_creditor_id] ? 'mdi-text-box-check-outline' : 'mdi-text-box-plus-outline' }}</v-icon>Ressalva
                            </v-btn>
                          </td>
                        </tr>
                      </template>
                    </tbody>
                  </template>
                </v-simple-table>

                <div class="ballot-footer">
                  <span>
                    <strong>{{ ballotVotedCount }}</strong> de <strong>{{ ballotTotalCount }}</strong> voto(s) registrado(s)
                    <template v-if="dataset.data.voting_status === 2">
                      · {{ ballotSelectedCount }} selecionado(s) para registrar
                    </template>
                  </span>
                  <v-btn v-if="dataset.data.voting_status === 2" color="primary" depressed class="ballot-btn ballot-btn--main" :loading="dataset.loading"
                    :disabled="!ballotSelectedCount" @click="submitBallot">
                    Registrar {{ ballotSelectedCount || '' }} voto(s)
                  </v-btn>
                </div>
              </template>
            </div>

            <template v-if="!isProcurator && dataset.data.voting_status === 2">
              <v-alert v-if="dataset.credits.length > 1" type="info" text dense class="mb-2">
                Você possui crédito em {{ dataset.credits.length }} classes neste caso. O voto é registrado separadamente em cada classe.
              </v-alert>

              <v-sheet v-for="credit in dataset.credits" :key="credit.user_creditor_id" class="vote-card mt-4" outlined>
                <v-card-text>
                  <div class="ballot-heading">
                    <div><span class="ballot-eyebrow">SUA CÉDULA</span><h3>{{ credit.class_name }}</h3></div>
                    <div class="credit-value"><span>Valor do crédito</span><strong>{{ formatCreditAmount(credit.amount, credit.currency) }}</strong></div>
                  </div>

                  <template v-if="credit.vote">
                    <div class="registered-vote" :class="'registered-vote--' + Number(credit.vote)" role="status">
                      <div class="registered-vote-info">
                        <div class="registered-vote-icon" aria-hidden="true">
                          <v-icon size="28">{{ Number(credit.vote) === 1 ? 'mdi-thumb-up-outline' : Number(credit.vote) === 2 ? 'mdi-thumb-down-outline' : 'mdi-minus-circle-outline' }}</v-icon>
                        </div>
                        <div class="registered-vote-copy">
                          <span class="registered-vote-label">VOTO REGISTRADO</span>
                          <strong class="registered-vote-choice">{{ voteLabel(credit.vote) }}</strong><span v-if="credit.vote_locked" class="d-block text-caption">Termo de adesão · voto irrevogável</span>
                          <span class="registered-vote-description">Sua escolha foi registrada nesta classe.</span>
                          <span v-if="credit.reservation" class="d-block text-caption mt-1">
                            <strong>Ressalva:</strong> {{ credit.reservation.text || '-' }}<template v-if="credit.reservation.doc_name"> · Documento: {{ credit.reservation.doc_name }}</template>
                          </span>
                        </div>
                      </div>
                      <div class="registered-vote-action">
                        <v-btn v-if="!credit.vote_locked" color="primary" text small :loading="dataset.loading" @click="restoreVote(credit)">
                          <v-icon left small>mdi-restore</v-icon>
                          Restaurar voto
                        </v-btn>
                        <span>Para escolher outra opção</span>
                      </div>
                    </div>
                  </template>

                  <template v-else>
                    <div class="vote-options" role="group" :aria-label="'Opções de voto para ' + credit.class_name">
                      <v-btn v-for="option in voteOptions" :key="option.value" outlined depressed
                        class="vote-option" :class="['vote-option--' + option.value, { 'vote-selected': pendingVotes[credit.user_creditor_id] === option.value }]"
                        :aria-pressed="pendingVotes[credit.user_creditor_id] === option.value ? 'true' : 'false'"
                        :disabled="dataset.loading" @click="setVote(credit, option.value)">
                        <span class="option-content">
                          <v-icon class="option-icon">{{ option.icon }}</v-icon>
                          <strong>{{ option.label }}</strong>
                          <span class="option-description">{{ option.description }}</span>
                        </span>
                        <v-icon v-if="pendingVotes[credit.user_creditor_id] === option.value" class="option-check" small>mdi-check-circle</v-icon>
                      </v-btn>
                    </div>
                    <div class="vote-reservation mt-3">
                      <template v-if="pendingReservations[credit.user_creditor_id]">
                        <v-chip small outlined color="primary" close @click="openReservation(credit)" @click:close="removeReservation(credit)">
                          <v-icon left small>mdi-text-box-check-outline</v-icon>Ressalva adicionada (editar)
                        </v-chip>
                      </template>
                      <v-btn v-else small text color="primary" :disabled="dataset.loading" @click="openReservation(credit)">
                        <v-icon left small>mdi-text-box-plus-outline</v-icon>Adicionar ressalva ou justificativa (opcional)
                      </v-btn>
                    </div>
                    <div class="vote-confirmation">
                      <div class="vote-selection-note" aria-live="polite">
                        <span class="confirmation-eyebrow">CONFIRMAÇÃO DO VOTO</span>
                        <template v-if="pendingVotes[credit.user_creditor_id]">
                          <strong>Opção selecionada: {{ voteOptions.find(option => option.value === pendingVotes[credit.user_creditor_id]).label }}</strong>
                          <span>Seu voto será registrado após a confirmação.</span>
                        </template>
                        <span v-else>Escolha sim, não ou abstenção.</span>
                      </div>
                      <v-btn color="primary" depressed class="confirm-vote" :loading="dataset.loading"
                        :disabled="!pendingVotes[credit.user_creditor_id]" @click="submitVote(credit)">
                        Confirmar voto<v-icon right small>mdi-arrow-right</v-icon>
                      </v-btn>
                    </div>
                  </template>
                </v-card-text>
              </v-sheet>
            </template>

            <template v-else-if="dataset.credits.length > 1">
              <v-simple-table dense class="mt-2">
                <template #default>
                  <thead>
                    <tr>
                      <th>Classe</th>
                      <th class="text-right">Crédito</th>
                      <th class="text-center">Seu voto</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="credit in dataset.credits" :key="credit.user_creditor_id">
                      <td>{{ credit.class_name }}</td>
                      <td class="text-right">{{ formatCreditAmount(credit.amount, credit.currency) }}</td>
                      <td class="text-center">
                        <strong :style="{ color: voteColor(credit.vote) }">
                          {{ credit.vote ? voteLabel(credit.vote) : 'Não votou' }}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </template>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="dataset.data && dataset.data.voting_status === 2">
      <v-col cols="12">
        <v-card class="card-form-outlined table-card voting-progress" flat>
          <v-card-title class="table-card-title">
            Participação na votação
          </v-card-title>
          <v-card-text>
            <v-progress-linear class="mb-2" :background-opacity="0.25" :value="votingProgressPercentage" :height="8" rounded
              color="primary"></v-progress-linear>
            <span>
              <strong>{{ totalVotes }} de {{ totalAllowedVotes }} voto(s) registrado(s)</strong>
            </span>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- GRAFICOS -->
    <v-row v-if="dataset.data && dataset.data.voting_status === 5">
      <v-col>
        <v-card class="card-form-outlined table-card">
          <v-card-title class="table-card-title">
            SIM
          </v-card-title>
          <v-card-text>
            <v-row align="center" class="mb-2" no-gutters>
              <v-col sm="12">
                <v-progress-linear class="mb-2" :background-opacity="0.4" :value="yesPercentage" :height="20"
                  color="green"></v-progress-linear>

                <span>
                  <strong> {{ yesPercentage.toFixed(1) }} %</strong>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col>
        <v-card class="card-form-outlined table-card">
          <v-card-title class="table-card-title">
            NÃO
          </v-card-title>
          <v-card-text>
            <v-row align="center" class="mb-2" no-gutters>
              <v-col sm="12">
                <v-progress-linear class="mb-2" :background-opacity="0.4" :value="noPercentage" :height="20"
                  color="red"></v-progress-linear>

                <span>
                  <strong> {{ noPercentage.toFixed(1) }} %</strong>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col>
        <v-card class="card-form-outlined table-card">
          <v-card-title class="table-card-title">
            ABSTENÇÃO
          </v-card-title>
          <v-card-text>
            <v-row align="center" class="mb-2" no-gutters>
              <v-col sm="12">
                <v-progress-linear class="mb-2" :background-opacity="0.4" :value="nullPercentage" :height="20"
                  color="#ccc"></v-progress-linear>

                <span>
                  <strong> {{ nullPercentage.toFixed(1) }}%</strong>
                </span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

    </v-row>

    <v-row class="mt-6">
      <v-col>
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn class="ml-4" v-on="on" type="button" elevation="0" color="primary" outlined
              @click="$router.go(-1)">Voltar</v-btn>
            <v-btn text color="primary" class="ml-2" to="/assembleia-credores">
              <v-icon left small>mdi-account-group-outline</v-icon> Ir para a assembleia
            </v-btn>
          </template>
          <span>Retornar para Assembleia de Credores</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- Voto igual em todos os pendentes: confirma antes de trocar as opcoes marcadas -->
    <v-dialog v-model="bulkConfirm.open" max-width="560px" scrollable>
      <v-card v-if="bulkConfirm.option" class="bulk-confirm">
        <v-card-title class="bulk-confirm__title">
          <span>Marcar a mesma opção em todos?</span>
          <v-spacer />
          <v-btn icon small aria-label="Fechar" @click="bulkConfirm.open = false"><v-icon small>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="bulk-confirm__body">
          <div class="bulk-confirm__choice" :class="'bulk-confirm__choice--' + bulkConfirm.option.value">
            <v-icon>{{ bulkConfirm.option.icon }}</v-icon>
            <div>
              <div class="bulk-confirm__choice-label">{{ bulkConfirm.option.label.toUpperCase() }}</div>
              <div class="bulk-confirm__choice-sub">em {{ bulkConfirm.items.length }} crédito(s) ainda sem voto</div>
            </div>
          </div>

          <v-alert v-if="bulkConfirm.replaced" type="warning" text dense class="mt-4 mb-0">
            {{ bulkConfirm.replaced }} crédito(s) já tinham outra opção marcada e passarão para {{ bulkConfirm.option.label.toUpperCase() }}.
          </v-alert>

          <div class="bulk-confirm__list-title">Créditos</div>
          <div class="bulk-confirm__list">
            <div v-for="item in bulkConfirm.items" :key="item.user_creditor_id" class="bulk-confirm__item">
              <div class="bulk-confirm__item-main">
                <strong>{{ item.creditor_name }}</strong>
                <span>{{ item.class_name }}</span>
              </div>
              <span class="bulk-confirm__item-amount">{{ formatCreditAmount(item.amount, item.currency) }}</span>
            </div>
          </div>

          <div class="bulk-confirm__note">
            <v-icon small>mdi-information-outline</v-icon>
            <span>Isto só marca a opção. Os votos são registrados quando você clicar em <strong>Registrar votos</strong>.</span>
          </div>
        </v-card-text>
        <v-card-actions class="bulk-confirm__actions">
          <v-spacer />
          <v-btn text @click="bulkConfirm.open = false">Cancelar</v-btn>
          <v-btn depressed dark :color="bulkConfirm.option.value === 'yes' ? 'green darken-1' : (bulkConfirm.option.value === 'no' ? 'red darken-1' : 'grey darken-1')" @click="applyBulkConfirm">
            <v-icon left small>{{ bulkConfirm.option.icon }}</v-icon>Marcar {{ bulkConfirm.option.label.toUpperCase() }} em todos
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ressalva/justificativa do voto: vai com o voto, aparece no laudo e no anexo da ata -->
    <v-dialog v-model="reservationDialog.open" max-width="560">
      <v-card>
        <v-card-title class="text-h6">Ressalva ou justificativa do voto</v-card-title>
        <v-card-text>
          <p class="text-body-2 mb-3" v-if="reservationDialog.credit">{{ reservationDialog.credit.class_name }}</p>
          <v-textarea v-model="reservationDialog.text" outlined auto-grow rows="4" counter="5000" maxlength="5000"
            label="Texto da ressalva" placeholder="Descreva a ressalva ou a justificativa do seu voto"></v-textarea>
          <v-file-input v-model="reservationDialog.file" outlined dense accept=".pdf,image/*" prepend-icon="" append-icon="mdi-paperclip"
            label="Documento (opcional)" hint="PDF ou imagem, até 10 MB" persistent-hint></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="reservationDialog.open = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :disabled="!reservationDialog.text.trim() && !reservationDialog.file" @click="saveReservation">Usar no voto</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { eventHub } from '@/main';

import axios from 'axios';
import itemTypes from "@/common/data/item-types.data.js";
import { cpf, cnpj } from 'cpf-cnpj-validator';
export default {
  name: 'VotingPage',

  data() {
    return {
      voteOptions: [
        { value: 'yes', label: 'Sim', description: 'A favor da proposta', icon: 'mdi-thumb-up-outline' },
        { value: 'no', label: 'Não', description: 'Contra a proposta', icon: 'mdi-thumb-down-outline' },
        { value: 'null', label: 'Abstenção', description: 'Não manifestar posição', icon: 'mdi-minus-circle-outline' },
      ],
      totalVotes: 0,
      totalAllowedVotes: 0,
      yesVotes: 0,
      noVotes: 0,
      nullVotes: 0,

      intervalId: null,

      vote: null,
      // Opcao de voto escolhida (ainda nao salva) por linha do QGC: { [user_creditor_id]: 'yes' | 'no' | 'null' }
      pendingVotes: {},
      // Ressalva escolhida para cada credito (user_creditor_id) ainda nao votado.
      pendingReservations: {},
      // Confirmacao do voto igual em todos os pendentes.
      bulkConfirm: { open: false, option: null, items: [], replaced: 0 },
      reservationDialog: { open: false, credit: null, text: '', file: null },
      // Cedula do procurador: credores representados, cada um com uma entrada por classe.
      ballot: {
        creditors: [],
      },
      meeting: {},
      user: null,
      valid: true,
      panel: [0],
      e1: 1,
      participationType: '',
      clientId: null,
      meetingInfo: null,
      selectedCreditorId: null,
      dataset: {
        itemTypes: itemTypes,
        loading: false,
        generalMeeting: {},
        data: {
          case_name: null,
          general_meeting_name: null,
          voting_id: null,
          voting_name: null,
          voting_description: null,
          voting_status: null,
        },
        votingResult: null,
        // Uma entrada por classe em que o credor tem credito e presenca nesta assembleia.
        credits: [],
        isFirstLoad: true,
      },
    };
  },


  created() {
    //this.user = this.$root.user;

    // if (!this.$route.query.id)
    //   this.$router.push(`/assembleia-credores/votacao`)

    if (this.$root.user) {
      this.user = this.$root.user;
    }

    this.selectedCreditorId = this.$route.query.creditor_user_id || null;

  },

  mounted() {
    if (this.canShowVoting) this.loadVotingResult();
  },

  destroyed() {
    this.stopAutoUpdate();
  },

  computed: {
    yesPercentage() {
      return this.calculatePercentage(this.yesVotes);
    },

    noPercentage() {
      return this.calculatePercentage(this.noVotes);
    },

    nullPercentage() {
      return this.calculatePercentage(this.nullVotes);
    },

    votingProgressPercentage() {
      if (!this.totalAllowedVotes) return 0;
      return Math.min((this.totalVotes / this.totalAllowedVotes) * 100, 100);
    },

    isProcurator() {
      return !!this.user && Number(this.user.type) === 2;
    },

    canShowVoting() {
      // Procurador ve a cedula com todos os credores representados; credor ve as proprias classes.
      return true;
    },

    ballotCredits() {
      return (this.ballot.creditors || []).reduce((items, creditor) => items.concat(creditor.credits || []), []);
    },

    ballotTotalCount() {
      return this.ballotCredits.length;
    },

    ballotVotedCount() {
      return this.ballotCredits.filter((credit) => !!credit.vote).length;
    },

    ballotPendingCount() {
      return this.ballotCredits.filter((credit) => !credit.vote).length;
    },

    ballotSelectedCount() {
      return this.ballotCredits.filter((credit) => !credit.vote && this.pendingVotes[credit.user_creditor_id]).length;
    },

    pendingCreditsCount() {
      return (this.dataset.credits || []).filter((credit) => !credit.vote).length;
    },
  },

	  watch: {
	    '$route.params.id'() {
	      this.resetVotingState();
	      if (this.canShowVoting) this.loadVotingResult();
	    },

	    selectedCreditorId() {
	      this.resetVotingState();

	      if (this.canShowVoting) this.loadVotingResult();
	    },
  },


	  methods: {

	    resetVotingState() {
	      this.stopAutoUpdate();
	      this.vote = null;
	      this.totalVotes = 0;
	      this.totalAllowedVotes = 0;
	      this.yesVotes = 0;
	      this.noVotes = 0;
	      this.nullVotes = 0;
	      this.dataset.votingResult = null;
	      this.dataset.credits = [];
	      this.pendingVotes = {};
	      this.ballot.creditors = [];
	      this.dataset.isFirstLoad = true;
	      this.dataset.data = {
	        case_name: null,
	        general_meeting_name: null,
	        voting_id: null,
	        voting_name: null,
	        voting_description: null,
	        voting_status: null,
	        vote: null,
	      };
	    },

	    setVote(credit, value) {
      this.vote = null;
      this.$set(this.pendingVotes, credit.user_creditor_id, value);
    },

    voteIconFor(value) {
      switch (value) {
        case 'yes':
          return { icon: 'mdi-thumb-up', color: 'green' };
        case 'no':
          return { icon: 'mdi-thumb-down', color: 'red' };
        case 'null':
          return { icon: 'mdi-cancel', color: 'grey' };
        default:
          return { icon: 'mdi-progress-alert', color: 'warning' };
      }
    },

    formatCreditAmount(amount, currency) {
      const value = Number(amount);
      if (!Number.isFinite(value)) return 'N/A';
      const codes = { 1: 'BRL', 2: 'USD', 3: 'EUR' };
      const code = codes[Number(currency)] || 'BRL';
      return value.toLocaleString('pt-BR', { style: 'currency', currency: code });
    },

    formatRegistration(value) {
      const digits = String(value || '').replace(/\D/g, '');
      if (!digits) return '';
      return digits.length > 11 ? cnpj.format(digits) : cpf.format(digits);
    },

    applyToAllPending(value) {
      this.ballotCredits
        .filter((credit) => !credit.vote)
        .forEach((credit) => this.$set(this.pendingVotes, credit.user_creditor_id, value));
    },

    // Antes de marcar a mesma opcao em todos os pendentes: mostra a opcao, os
    // creditos e quantas escolhas feitas linha a linha serao trocadas.
    confirmApplyToAll(value) {
      const option = this.voteOptions.find((item) => item.value === value);
      if (!option) return;
      const items = [];
      (this.ballot.creditors || []).forEach((creditor) => {
        (creditor.credits || []).filter((credit) => !credit.vote).forEach((credit) => {
          items.push({ ...credit, creditor_name: creditor.name });
        });
      });
      const replaced = items.filter((credit) => this.pendingVotes[credit.user_creditor_id] && this.pendingVotes[credit.user_creditor_id] !== value).length;
      this.bulkConfirm = { open: true, option, items, replaced };
    },

    applyBulkConfirm() {
      const { option, items } = this.bulkConfirm;
      this.applyToAllPending(option.value);
      this.bulkConfirm.open = false;
      this.$dialog.notify.info(`${option.label.toUpperCase()} marcado em ${items.length} crédito(s). Confira e clique em Registrar votos.`, { position: 'top-right', timeout: 5000 });
    },

    clearPendingSelection() {
      this.ballotCredits
        .filter((credit) => !credit.vote)
        .forEach((credit) => this.$delete(this.pendingVotes, credit.user_creditor_id));
    },

    async loadBallot() {
      try {
        const response = await axios.get('/api/general-meeting-votings/procurator-ballot', {
          params: { general_meeting_voting_id: this.$route.params.id }
        });

        const content = response.data.content || {};
        const totals = (content.data && content.data[0]) || {};
        const assembly = (content.assembly && content.assembly[0]) || {};

        this.dataset.votingResult = content.data;
        this.totalVotes = totals.total_votes || 0;
        this.totalAllowedVotes = totals.total_allowed || 0;
        this.yesVotes = totals.total_vote_1 || 0;
        this.noVotes = totals.total_vote_2 || 0;
        this.nullVotes = totals.total_vote_3 || 0;

        this.dataset.data.general_meeting_name = assembly.gm_name;
        this.dataset.data.voting_name = assembly.gmv_name;
        this.dataset.data.voting_description = assembly.gmv_description;
        this.dataset.data.client_name = assembly.client_name;
        this.dataset.data.voting_status = assembly.gmv_status;
        this.ballot.creditors = Array.isArray(content.creditors) ? content.creditors : [];
        this.dataset.isFirstLoad = false;

        if (Number(this.dataset.data.voting_status) === 2) {
          this.startAutoUpdate();
        } else {
          this.stopAutoUpdate();
        }
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
        this.stopAutoUpdate();
      }
    },

    async submitBallot() {
      const selectedCredits = this.ballotCredits
        .filter((credit) => !credit.vote && this.pendingVotes[credit.user_creditor_id]);
      const votes = selectedCredits.map((credit) => {
          const choice = this.pendingVotes[credit.user_creditor_id];
          return {
            user_creditor_id: credit.user_creditor_id,
            creditor_user_id: credit.creditor_user_id,
            vote: choice === 'yes' ? 1 : (choice === 'no' ? 2 : 3),
          };
        });

      if (!votes.length) {
        this.$dialog.notify.error("Selecione ao menos um voto!", { position: 'top-right', timeout: 5000 });
        return;
      }

      const proceed = await this.$dialog.confirm({
        title: "Registrar votos",
        text: `Deseja registrar ${votes.length} voto(s) selecionado(s)?`,
        actions: {
          false: "Cancelar",
          true: "Registrar",
        },
      });

      if (!proceed) return;

      this.dataset.loading = true;

      try {
        // Ressalvas da cedula: documento sobe antes, o voto leva a referencia.
        for (let i = 0; i < selectedCredits.length; i += 1) {
          Object.assign(votes[i], await this.reservationPayload(selectedCredits[i]));
        }
        const response = await axios.post('/api/general-meeting-votings/batch', {
          general_meeting_voting_id: this.$route.params.id,
          votes,
        });

        const content = response.data.content || {};
        const saved = Array.isArray(content.saved) ? content.saved : [];
        const errors = Array.isArray(content.errors) ? content.errors : [];

        saved.forEach((id) => { this.$delete(this.pendingVotes, id); this.$delete(this.pendingReservations, id); });

        if (saved.length) {
          this.$dialog.notify.success(`${saved.length} voto(s) registrado(s) com sucesso!`, { position: 'top-right', timeout: 5000 });
        }

        if (errors.length) {
          const messages = [...new Set(errors.map((item) => item.message))].join('; ');
          this.$dialog.notify.error(`${errors.length} voto(s) não registrado(s): ${messages}`, { position: 'top-right', timeout: 8000 });
        }

        await this.loadBallot();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 5000
        });
      } finally {
        this.dataset.loading = false;
      }
    },

    voteLabel(vote) {
      switch (vote) {
        case 1:
          return 'SIM';
        case 2:
          return 'NÃO';
        case 3:
          return 'ABSTENÇÃO';
        default:
          return 'Indefinido';
      }
    },

    voteColor(vote) {
      switch (vote) {
        case 1:
          return 'green';
        case 2:
          return 'red';
        case 3:
          return 'grey';
        default:
          return 'black';
      }
    },

    calculatePercentage(votes) {

      if (this.totalVotes === 0) {
        return 0;
      }
      return (votes / this.totalVotes) * 100;

    },

    resolveColorDeadlineAccreditation(status) {

      if (status > 0 && status < 50)
        return '#FFCC80';

      if (status > 50 && status < 80)
        return '#F9AA33';

      if (status > 80 && status < 90)
        return '#FF5252';

      return '#FF5252';

    },


    openReservation(credit) {
      const current = this.pendingReservations[credit.user_creditor_id] || {};
      this.reservationDialog = { open: true, credit, text: current.text || '', file: current.file || null };
    },

    saveReservation() {
      const { credit, text, file } = this.reservationDialog;
      if (file && file.size > 10 * 1024 * 1024) {
        this.$dialog.notify.error("O documento deve ter até 10 MB.", { position: 'top-right', timeout: 5000 });
        return;
      }
      this.$set(this.pendingReservations, credit.user_creditor_id, { text: String(text || '').trim(), file: file || null });
      this.reservationDialog.open = false;
    },

    removeReservation(credit) {
      this.$delete(this.pendingReservations, credit.user_creditor_id);
    },

    // Campos da ressalva para o voto (sobe o documento antes). Sem ressalva, nao envia nada.
    async reservationPayload(credit) {
      const reservation = this.pendingReservations[credit.user_creditor_id];
      if (!reservation) return {};
      let userDocId = null;
      if (reservation.file) {
        const data = new FormData();
        data.append('file', reservation.file);
        data.append('name', reservation.file.name);
        data.append('type', '3');
        const response = await axios.post('/api/user-docs', data);
        const doc = response.data.document || (response.data.content && response.data.content.document);
        userDocId = doc ? doc.id : null;
      }
      return { reservation_text: reservation.text, reservation_user_doc_id: userDocId };
    },

    async submitVote(credit) {

      const choice = credit ? this.pendingVotes[credit.user_creditor_id] : null;

      if (!choice) {
        this.$dialog.notify.error("Selecione uma opção de voto!", { position: 'top-right', timeout: 5000 });
        return;
      }

      this.dataset.loading = true;

      const payload = {
        general_meeting_voting_id: this.$route.params.id,
        user_creditor_id: credit.user_creditor_id,
        vote: choice === "yes" ? 1 : choice === "no" ? 2 : choice === "null" ? 3 : null
      };

      if (this.selectedCreditorId) payload.creditor_user_id = this.selectedCreditorId;

      try {
        Object.assign(payload, await this.reservationPayload(credit));
        await axios.post('/api/general-meeting-votings', payload)
        this.$delete(this.pendingReservations, credit.user_creditor_id);

        this.$dialog.notify.success("Voto contabilizado com sucesso!", { position: 'top-right', timeout: 5000 });

        credit.vote = payload.vote;
        this.$delete(this.pendingVotes, credit.user_creditor_id);

        this.loadVotingResult();

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 5000
        });
      } finally {
        this.dataset.loading = false;
      }
    },

    async restoreVote(credit) {
      const classHint = credit && credit.class_name && this.dataset.credits.length > 1
        ? ` na ${credit.class_name}`
        : '';
      const proceed = await this.$dialog.confirm({
        title: "Restaurar voto",
        text: `Deseja restaurar seu voto${classHint}? Você poderá votar novamente enquanto a votação estiver em andamento.`,
        actions: {
          false: "Cancelar",
          true: "Restaurar",
        },
      });

      if (!proceed) return;

      this.dataset.loading = true;

      const payload = {
        general_meeting_voting_id: this.$route.params.id,
      };

      if (credit && credit.user_creditor_id) payload.user_creditor_id = credit.user_creditor_id;
      if (credit && credit.creditor_user_id) payload.creditor_user_id = credit.creditor_user_id;
      else if (this.selectedCreditorId) payload.creditor_user_id = this.selectedCreditorId;

      try {
        await axios.post('/api/general-meeting-votings/restore', payload);
        this.dataset.data.vote = null;
        if (credit) credit.vote = null;
        this.vote = null;
        this.$dialog.notify.success("Voto restaurado com sucesso.", { position: 'top-right', timeout: 5000 });
        await this.loadVotingResult();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 5000
        });
      } finally {
        this.dataset.loading = false;
      }
    },

    startAutoUpdate() {
      if (!this.intervalId) {
        this.intervalId = setInterval(() => {
          this.loadVotingResult();
        }, 5000); // 5 segundos
      }
    },

    stopAutoUpdate() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    async loadVotingResult() {

      if (this.isProcurator) return this.loadBallot();

      try {
        if (!this.canShowVoting) return;

        const general_meeting_voting_id = this.$route.params.id;

        const action = this.dataset.isFirstLoad
        const params = {
          general_meeting_voting_id,
          action
        };

        if (this.selectedCreditorId) params.creditor_user_id = this.selectedCreditorId;

        const response = await axios.get(`/api/general-meeting-votings-request`, {
          params
        });

        this.dataset.votingResult = response.data.content.data;

        if(!response.data.content.assembly){

          this.$dialog.notify.error("Você não tem acesso a esta votação!", { position: 'top-right', timeout: 5000 });

          this.stopAutoUpdate();

          return;

        }else{

          this.totalVotes = response.data.content.data[0].total_votes
          this.totalAllowedVotes = response.data.content.data[0].total_allowed || 0
          this.yesVotes = response.data.content.data[0].total_vote_1 || 0
          this.noVotes = response.data.content.data[0].total_vote_2 || 0
          this.nullVotes = response.data.content.data[0].total_vote_3 || 0

          this.dataset.data.general_meeting_name = response.data.content.assembly[0].gm_name
          this.dataset.data.voting_name = response.data.content.assembly[0].gmv_name
          this.dataset.data.voting_description = response.data.content.assembly[0].gmv_description
          this.dataset.data.client_name = response.data.content.assembly[0].client_name
          this.dataset.data.vote = response.data.content.vote
          this.dataset.credits = Array.isArray(response.data.content.credits) ? response.data.content.credits : []
          this.dataset.data.voting_status = response.data.content.assembly[0].gmv_status

        }

        this.dataset.isFirstLoad = false;

        if (Number(this.dataset.data.voting_status) === 2) {
          this.startAutoUpdate();
        } else {
          this.stopAutoUpdate();
        }

      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), {
          position: 'top-right',
          timeout: 2000
        });

        this.stopAutoUpdate();
      }
    },

  }
};

</script>


<style scoped>
.vote-card {
  border: 1px solid #1976d2;
  padding: 16px;
  border-radius: 10px;
}

.vote-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.vote-options .vote-chip {
  min-width: 120px;
  display: flex;
  justify-content: center;
  cursor: pointer;
  border: 2px solid transparent;
  border-radius: 8px;
  transition: background-color 0.3s, border-color 0.3s;
}

.bar-container {
  width: 100%;
  height: 200px;
  background-color: #e0e0e0;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.bar {
  width: 50px;
}

.yes-bar {
  background-color: #4caf50;
}

.no-bar {
  background-color: #f44336;
}

.null-bar {
  background-color: #ffeb3b;
}

.v-card-title {
  background-color: #fff;
  color: #000;
}

.v-card {
  background-color: #fff;
  color: #000;
}

.no-border-banner {
  border: none;
  box-shadow: none;
  background: none;
}

.vote-options {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.registered-vote {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 12px;
}

.registered-vote-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
</style>

<style scoped>
.bulk-confirm__title { font-size: 18px; font-weight: 700; color: #252538; padding-bottom: 8px; }
.bulk-confirm__body { padding-top: 4px !important; }
.bulk-confirm__choice { display: flex; align-items: center; gap: 14px; padding: 14px 16px; border-radius: 8px; border: 1px solid; }
.bulk-confirm__choice .v-icon { font-size: 30px; }
.bulk-confirm__choice--yes { background: #ECFDF3; border-color: #A6E9C0; }
.bulk-confirm__choice--yes .v-icon, .bulk-confirm__choice--yes .bulk-confirm__choice-label { color: #15803D; }
.bulk-confirm__choice--no { background: #FEF2F2; border-color: #F5B5B5; }
.bulk-confirm__choice--no .v-icon, .bulk-confirm__choice--no .bulk-confirm__choice-label { color: #B91C1C; }
.bulk-confirm__choice--null { background: #F3F4F6; border-color: #D1D5DB; }
.bulk-confirm__choice--null .v-icon, .bulk-confirm__choice--null .bulk-confirm__choice-label { color: #4B5563; }
.bulk-confirm__choice-label { font-size: 20px; font-weight: 800; line-height: 1.1; }
.bulk-confirm__choice-sub { font-size: 13px; color: #4B5563; }
.bulk-confirm__list-title { margin: 16px 0 6px; font-size: 12px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; color: #6B7280; }
.bulk-confirm__list { max-height: 220px; overflow-y: auto; border: 1px solid #E5E7EB; border-radius: 6px; }
.bulk-confirm__item { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 8px 12px; border-bottom: 1px solid #F0F1F3; font-size: 13px; }
.bulk-confirm__item:last-child { border-bottom: 0; }
.bulk-confirm__item-main { display: flex; flex-direction: column; min-width: 0; }
.bulk-confirm__item-main strong { color: #252538; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.bulk-confirm__item-main span { color: #6B7280; }
.bulk-confirm__item-amount { white-space: nowrap; color: #374151; }
.bulk-confirm__note { display: flex; align-items: flex-start; gap: 8px; margin-top: 14px; font-size: 13px; color: #4B5563; }
.bulk-confirm__actions { padding: 8px 16px 16px !important; }

/* Cedula do procurador: respiro entre os blocos e linhas mais altas */
.ballot-section { padding-top: 28px; }
.ballot-notice { display: flex; align-items: center; gap: 10px; padding: 12px 16px; margin-bottom: 20px; border-radius: 12px; background: rgba(120, 119, 229, .09); color: #2a1a40; font-size: 14px; }
.bulk-vote { margin-bottom: 28px; border: 1px solid #ebe5f1; border-radius: 14px; background: #faf9fd; }
.bulk-vote__header { padding: 20px 22px 6px; }
.bulk-vote__title { font-weight: 700; color: #210b38; font-size: 15px; margin-bottom: 6px; }
.bulk-vote__text { font-size: 13px; line-height: 1.6; color: #5e5667; }
.bulk-vote__actions { display: flex; flex-wrap: wrap; gap: 10px; padding: 12px 22px 20px; }
.bulk-vote__footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 22px; border-top: 1px solid #ebe5f1; font-size: 13px; color: #4a3a5c; }
.bulk-vote__footer-actions { display: flex; align-items: center; gap: 8px; }
.ballot-btn { height: 38px !important; padding: 0 16px !important; border-radius: 10px; text-transform: none; letter-spacing: 0; font-weight: 600; }
.ballot-btn--main { height: 42px !important; padding: 0 22px !important; }
.ballot-table ::v-deep th { font-size: 11px !important; font-weight: 700 !important; letter-spacing: .06em; text-transform: uppercase; color: #8b769d !important; border-bottom: 1px solid #ebe5f1 !important; padding-top: 4px !important; padding-bottom: 10px !important; }
.ballot-table ::v-deep td { padding-top: 14px !important; padding-bottom: 14px !important; height: auto !important; border-bottom: 1px solid #f1edf5 !important; font-size: 14px; color: #25113c; vertical-align: middle; }
.ballot-table ::v-deep tbody tr:hover { background: #faf9fd !important; }
.ballot-table ::v-deep td .caption { margin-top: 2px; }
.ballot-table ::v-deep tbody tr:last-child td { border-bottom: 0 !important; }
.ballot-table ::v-deep .v-btn { text-transform: none; letter-spacing: 0; font-weight: 600; }
.ballot-vote-options { display: inline-flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
.ballot-cards { display: flex; flex-direction: column; gap: 12px; }
.ballot-card { padding: 16px; border: 1px solid #ebe5f1; border-radius: 14px; background: #fff; }
.ballot-card__head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
.ballot-card__creditor { display: flex; flex-direction: column; min-width: 0; }
.ballot-card__creditor strong { color: #210b38; font-size: 15px; overflow-wrap: anywhere; }
.ballot-card__creditor span { color: #8b769d; font-size: 12px; }
.ballot-card__amount { font-weight: 700; color: #25113c; white-space: nowrap; font-size: 14px; }
.ballot-card__class { margin-top: 6px; font-size: 13px; color: #5e5667; }
.ballot-card__voted { display: flex; align-items: center; flex-wrap: wrap; gap: 6px 10px; margin-top: 12px; font-size: 12px; color: #5e5667; }
.ballot-card__options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-top: 14px; }
.ballot-card__action { text-transform: none; letter-spacing: 0; font-weight: 600; padding: 0 4px !important; }
.ballot-option { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; min-height: 56px; border: 1px solid #ddd5e5; border-radius: 10px; background: #fff; font-size: 12px; font-weight: 700; cursor: pointer; transition: background-color .15s, border-color .15s; }
.ballot-option .v-icon { color: inherit !important; }
.ballot-option--yes { color: #25733d; }
.ballot-option--no { color: #b12f3d; }
.ballot-option--null { color: #5d526d; }
.ballot-option--on { border-width: 2px; border-color: currentColor; }
.ballot-option--yes.ballot-option--on { background: #e8f4eb; }
.ballot-option--no.ballot-option--on { background: #fcecee; }
.ballot-option--null.ballot-option--on { background: #f1edf5; }
.ballot-option:focus-visible { outline: 3px solid #8b77b7; outline-offset: 2px; }
.ballot-footer { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 14px; margin-top: 24px; padding-top: 20px; border-top: 1px solid #ebe5f1; font-size: 14px; color: #4a3a5c; }
@media (max-width: 600px) {
  .ballot-section { padding-top: 20px; }
  .bulk-vote__header { padding: 16px 16px 4px; }
  .bulk-vote__actions { padding: 10px 16px 16px; }
  .bulk-vote__footer { padding: 12px 16px; }
  .ballot-footer .ballot-btn--main { width: 100%; }
}

.voting-page { padding-bottom: 40px !important; }
.voting-page > .padding-block { height: 24px; }
.voting-panel { border: 1px solid #e8e3ed; border-radius: 16px !important; overflow: hidden; }
.voting-panel > .v-card__title { padding: 24px 28px 8px; font-size: 14px; letter-spacing: .06em; text-transform: uppercase; color: #72677e; }
.voting-panel > .v-card__text { padding: 16px 28px 28px; }
.meeting-summary { padding-bottom: 22px; border-bottom: 1px solid #eee9f2; }
.meeting-context { font-size: 13px; color: #716779; overflow-wrap: anywhere; }
.meeting-context span { padding: 0 10px; color: #b4aabd; }
.meeting-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-top: 10px; }
.meeting-heading h2 { color: #210b38; font-size: 26px; line-height: 1.3; overflow-wrap: anywhere; }
.meeting-description { margin: 12px 0 0; color: #5e5667; line-height: 1.6; white-space: pre-line; overflow-wrap: anywhere; }
.voter-status { display: flex; align-items: center; gap: 10px; padding: 18px 0 2px; color: #5e5667; }
.voting-panel .vote-card { padding: 8px; border: 1px solid #e6dfed; border-radius: 12px; background: #fcfbfd; }
.vote-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; margin-top: 22px; }
.vote-options .vote-option { min-width: 0; min-height: 64px; border-radius: 10px; text-transform: none; font-size: 16px; letter-spacing: 0; background: white; }
.vote-option--yes { color: #25733d !important; }
.vote-option--no { color: #b12f3d !important; }
.vote-option--null { color: #5d526d !important; }
.vote-options .vote-selected { border-width: 2px; background: #eee8f7; font-weight: 700; }
.vote-option--yes.vote-selected { background: #e8f4eb; }
.vote-option--no.vote-selected { background: #fcecee; }
.vote-option:focus-visible { outline: 3px solid #8b77b7; outline-offset: 3px; }
.vote-confirmation { display: flex; justify-content: space-between; align-items: center; gap: 20px; margin-top: 24px; padding-top: 20px; border-top: 1px solid #e6dfed; }
.vote-selection-note { display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: #716779; }
.vote-selection-note strong { font-size: 14px; color: #352442; }
.confirm-vote { height: 44px !important; border-radius: 8px; text-transform: none; letter-spacing: 0; }
.voting-progress { border: 1px solid #e8e3ed; border-radius: 12px !important; }
.voting-progress .v-card__title { font-size: 17px; padding-bottom: 12px; }
.registered-vote { background: white; margin: 16px 0 0; padding: 18px; }
@media (max-width: 600px) {
  .voting-panel > .v-card__title { padding: 20px 16px 8px; }
  .voting-panel > .v-card__text { padding: 12px 16px 20px; }
  .meeting-heading h2 { font-size: 22px; }
  .vote-options { gap: 6px; }
  .vote-options .vote-option { height: 64px; padding: 0 4px; font-size: 12px; }
  .vote-option .v-icon { display: none; }
  .vote-confirmation { align-items: stretch; flex-direction: column; gap: 14px; }
  .registered-vote { align-items: stretch; flex-direction: column; }
}

.voting-page { position: relative; max-width: 1120px; }
.voting-intro { padding: 12px 0 24px; }
.voting-eyebrow { display: flex; align-items: center; gap: 8px; color: #6e5c80; font-size: 12px; font-weight: 600; letter-spacing: .06em; }
.voting-intro h1 { font-size: clamp(26px, 3.2vw, 38px); line-height: 1.2; letter-spacing: -.035em; color: #210b38; margin: 12px 0 10px; }
.voting-intro h1 span { color: #776490; }
.voting-intro p { color: #716779; margin: 0; font-size: 14px; }
.voting-panel { box-shadow: 0 18px 55px -24px rgba(40, 16, 65, .24) !important; }
.voting-panel > .v-card__text { padding: 0 28px 28px; }
.meeting-summary { position: relative; isolation: isolate; margin: 0 -28px 0; padding: 30px 32px; background: #25113c; color: white; overflow: hidden; border: 0; }
.meeting-eyebrow { display: block; color: #c7b6de; font-size: 10px; letter-spacing: .16em; font-weight: 700; margin-bottom: 14px; }
.meeting-context { color: #d8cce4; font-size: 13px; }
.meeting-heading h2 { color: #fff; font-size: clamp(22px, 2.7vw, 30px); font-weight: 600; letter-spacing: -.02em; max-width: 100%; }
.meeting-description { color: #e1d9e9; max-width: 740px; font-size: 14px; }
.meeting-summary .v-chip { color: #fff !important; border-color: #9782ab !important; background: #ffffff08 !important; }
.meeting-summary .live-status { color: #c1edcf !important; border-color: #739e80 !important; }
.voter-status { padding: 22px 0 8px; font-size: 14px; }
.voting-panel .vote-card { background: white; padding: 12px; border-color: #e9e2ef; border-radius: 14px; }
.ballot-heading { display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; align-items: center; }
.ballot-eyebrow, .confirmation-eyebrow { font-size: 10px; letter-spacing: .12em; font-weight: 700; color: #857390; }
.ballot-heading h3 { color: #2b183d; font-size: 18px; margin: 5px 0 0; line-height: 1.4; }
.credit-value { background: #f6f3f9; border-radius: 8px; padding: 10px 14px; display: flex; flex-direction: column; gap: 4px; }
.credit-value span { color: #776885; font-size: 11px; }
.credit-value strong { color: #382449; font-size: 15px; font-variant-numeric: tabular-nums; }
.vote-options { gap: 14px; margin-top: 24px; }
.vote-options .vote-option { position: relative; min-height: 140px; border-color: #ddd5e5; border-radius: 12px; transition: background-color .18s, box-shadow .18s; }
.option-content { display: flex; flex-direction: column; align-items: center; gap: 10px; white-space: normal; }
.option-content strong { font-size: 18px; }
.option-icon { font-size: 28px; }
.option-description { font-size: 12px; font-weight: 400; color: #766b7d; }
.option-check { position: absolute; top: -48px; right: 0; }
.vote-options .vote-selected { box-shadow: 0 4px 16px #29113e10; border-color: currentColor; }
.vote-confirmation { border: 0; background: #f7f4fa; margin-top: 20px; padding: 18px; border-radius: 10px; }
.confirm-vote { min-width: 170px; height: 48px !important; }
.voting-progress { background: #faf8fc; }
@media (max-width: 600px) {
  .voting-intro { padding: 6px 4px 16px; }
  .voting-panel > .v-card__text { padding: 0 16px 16px; }
  .meeting-summary { margin: 0 -16px; padding: 24px 20px; }
  .meeting-heading { gap: 12px; }
  .voting-panel .vote-card { padding: 0; }
  .ballot-heading h3 { font-size: 16px; }
  .credit-value { width: 100%; flex-direction: row; justify-content: space-between; align-items: center; }
  .vote-options { gap: 6px; }
  .vote-options .vote-option { min-height: 106px; padding: 0 3px; }
  .option-content { gap: 8px; }
  .option-content strong { font-size: 12px; }
  .vote-option .option-icon { display: block; font-size: 24px; }
  .option-description { display: none; }
  .vote-option .option-check { display: none; }
  .vote-confirmation { padding: 14px; }
}
@media (prefers-reduced-motion: reduce) { .vote-options .vote-option { transition: none; } }

.registered-vote { --vote-accent: #625270; --vote-tint: #f5f2f8; background: var(--vote-tint); border: 1px solid #e7e0ec; border-left: 4px solid var(--vote-accent); border-radius: 12px; padding: 24px; margin-top: 24px; gap: 24px; }
.registered-vote--1 { --vote-accent: #24653b; --vote-tint: #f1f8f3; border-color: #d8e8dd; }
.registered-vote--2 { --vote-accent: #a03242; --vote-tint: #fcf3f4; border-color: #efdce0; }
.registered-vote-info { gap: 16px; }
.registered-vote-icon { width: 56px; height: 56px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: white; border-radius: 14px; }
.registered-vote-icon .v-icon { color: var(--vote-accent); }
.registered-vote-copy { display: flex; flex-direction: column; gap: 5px; }
.registered-vote-label { font-size: 10px; font-weight: 700; letter-spacing: .13em; color: #706577; }
.registered-vote-choice { font-size: 24px; line-height: 1.2; color: var(--vote-accent); letter-spacing: -.02em; }
.registered-vote-description { font-size: 13px; color: #625a68; }
.registered-vote-action { display: flex; flex-direction: column; align-items: center; gap: 4px; padding-left: 24px; border-left: 1px solid #ded8e3; }
.registered-vote-action .v-btn { text-transform: none; letter-spacing: 0; font-weight: 600; }
.registered-vote-action > span { font-size: 11px; color: #73677d; }
@media (max-width: 600px) {
  .registered-vote { padding: 18px; gap: 18px; }
  .registered-vote-info { align-items: flex-start; gap: 12px; }
  .registered-vote-icon { width: 44px; height: 44px; border-radius: 10px; }
  .registered-vote-choice { font-size: 22px; overflow-wrap: anywhere; }
  .registered-vote-action { padding: 14px 0 0; border-left: 0; border-top: 1px solid #ded8e3; }
}
</style>
